import { Popover, Snackbar, Stack, Tooltip, styled, useTheme } from '@mui/material'
import { copyTextToClipboard } from 'ComponentLibrary/src/common/clipboard'
import { ReactNode, useState } from 'react'
import IconButton from '../../atoms/Buttons/IconButton/IconButton'
import { Typography } from 'ComponentLibrary/src/atoms/typography'
import { Button } from '../../atoms/Buttons/Button'
import { Copy } from 'react-feather'
import { Alert } from 'ComponentLibrary/src/molecules/Alert'
import { useTranslation } from 'react-i18next'

type IconWithPopupProps = {
    icon: ReactNode
    label: string
    value: string
    linkTo?: string
    showTextButton?: boolean
}

const StyledButton = styled(Button)`
    text-transform: lowercase;
`

export function ContactIconWithPopup({ icon, label, value, linkTo, showTextButton }: IconWithPopupProps) {
    const [anchorEl, setAnchorEl] = useState<HTMLButtonElement | undefined>(undefined)
    const [snackbarOpen, setSnackbarOpen] = useState<boolean>(false)
    const theme = useTheme()
    const { t: tCommon } = useTranslation('terminology', { keyPrefix: 'common' })

    const handlePopoverOpen = (event: React.MouseEvent<HTMLButtonElement>) => setAnchorEl(event.currentTarget)
    const handlePopoverClose = () => setAnchorEl(undefined)

    const handleCopy = () => {
        void copyTextToClipboard(value)
        setSnackbarOpen(true)
    }
    const handleCloseSnackbar = () => setSnackbarOpen(false)

    return (
        <>
            {showTextButton ? (
                <StyledButton variant="text" onClick={handlePopoverOpen} label={label} startIcon={icon} />
            ) : (
                <IconButton icon={icon} onClick={handlePopoverOpen} />
            )}
            <PopoverContainer
                open={!!anchorEl}
                anchorEl={anchorEl}
                onClose={handlePopoverClose}
                anchorOrigin={{
                    vertical: 'top',
                    horizontal: 'right',
                }}
                transformOrigin={{
                    vertical: 'top',
                    horizontal: 'right',
                }}
            >
                <Stack direction="row" justifyContent="space-between" alignItems="center">
                    <Stack spacing={theme.spacing(2)}>
                        <Typography variant="bodyTextXSmall">{label}</Typography>
                        {linkTo ? (
                            <a href={linkTo} style={{ textDecoration: 'none' }}>
                                <Typography variant="headline3" color={theme.palette.primary2.green100}>
                                    {value}
                                </Typography>
                            </a>
                        ) : (
                            <Typography variant="headline3" color={theme.palette.customtext.darkblue100}>
                                {value}
                            </Typography>
                        )}
                    </Stack>

                    <Tooltip title={tCommon('actions.copy', { defaultValue: 'Kopieren' })}>
                        <Button startIcon={<Copy />} variant="secondary" onClick={handleCopy} />
                    </Tooltip>
                </Stack>
            </PopoverContainer>
            <Snackbar
                open={snackbarOpen}
                onClose={handleCloseSnackbar}
                autoHideDuration={5000}
                anchorOrigin={{ vertical: 'top', horizontal: 'center' }}
            >
                <div>
                    <Alert severity="success" size="small">
                        {tCommon('copiedToClipboard', { label, defaultValue: '{{label}} wurde in die Zwischenablage kopiert' })}
                    </Alert>
                </div>
            </Snackbar>
        </>
    )
}

const PopoverContainer = styled(Popover)(({ theme }) => ({
    '& .MuiPaper-root': {
        width: '304px',
        padding: theme.spacing(3),
        borderRadius: '3px',
        border: `1px solid ${theme.palette.secondary1.grey}`,
        boxShadow: '4px 3px 13px 2px rgba(0, 0, 0, 0.05)',
    },
}))
